import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Dialog } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import ConfirmDialog from "components/ConfirmDialog";
import DeleteIconButton from "components/IconButtons/DeleteIconButton";
import EditIconButton from "components/IconButtons/EditIconButton";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ListRequestOfOrganizationExternalReferenceAdminFilter,
  ListResponseOfOrganizationExternalReferenceModel,
  ListResponseOfString,
  OrganizationDetails,
  OrganizationExternalReferenceAdminFilter,
  OrganizationExternalReferenceModel,
  OrganizationExternalReferenceSaveRequest,
  ResponseCode,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import OrganizationExternalReferenceForm from "./OrganizationExternalReferenceForm";

const externalReferencesQueryKey = "organization-external-references";
const partnerNamesQueryKey = "organization-external-reference-partner-names";
const rusticiPartnerName = "rustici";

const isRustici = (partnerName?: string) =>
  partnerName?.toLowerCase() === rusticiPartnerName;

type OrganizationExternalReferencesProps = {
  organization: OrganizationDetails;
};

const useStyles = (theme: Theme) => ({
  header: {
    display: "flex",
    justifyContent: "end",
    margin: theme.spacing(4, 0, 3, 0),
    width: "100%",
  },
});

const OrganizationExternalReferences = ({
  organization,
}: OrganizationExternalReferencesProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { organizationService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("partnerName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [createOpen, createOpenActions] = useBoolean(false);
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const [confirmUpdateOpen, confirmUpdateOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [
    externalReference,
    setExternalReference,
  ] = useState<OrganizationExternalReferenceModel | null>(null);
  const [
    pendingUpdate,
    setPendingUpdate,
  ] = useState<OrganizationExternalReferenceSaveRequest | null>(null);

  const externalReferences = useStrictQuery<ListResponseOfOrganizationExternalReferenceModel>(
    [
      externalReferencesQueryKey,
      organization.id,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      organizationService.getOrganizationExternalReferences(
        ListRequestOfOrganizationExternalReferenceAdminFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: OrganizationExternalReferenceAdminFilter.fromJS({
            organizationId: organization.id,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const partnerNames = useStrictQuery<ListResponseOfString>(
    [partnerNamesQueryKey],
    () => organizationService.getOrganizationExternalReferencePartnerNames()
  );

  const cells: Array<TableCellType> = [
    {
      accessor: "partnerName",
      align: "left",
      type: "text",
      label: t({ id: "partnerNameLabel" }),
    },
    {
      accessor: "externalOrganizationId",
      align: "left",
      type: "text",
      label: t({ id: "externalOrganizationIdLabel" }),
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "node",
      noSort: true,
      View: ({ value }: { value: OrganizationExternalReferenceModel }) => (
        <>
          <EditIconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExternalReference(value);
              updateOpenActions.setTrue();
            }}
          />
          <DeleteIconButton
            size="small"
            onClick={() => {
              setExternalReference(value);
              deleteOpenActions.setTrue();
            }}
          />
        </>
      ),
      label: "",
    },
  ];

  const refresh = () => {
    void queryClient.invalidateQueries([
      externalReferencesQueryKey,
      organization.id,
    ]);
    void queryClient.invalidateQueries([partnerNamesQueryKey]);
  };

  const cleanup = () => {
    createOpenActions.setFalse();
    updateOpenActions.setFalse();
    confirmUpdateOpenActions.setFalse();
    deleteOpenActions.setFalse();
    setPendingUpdate(null);
  };

  const saveExternalReference = async (
    values: OrganizationExternalReferenceSaveRequest
  ) => {
    const result = await organizationService.saveOrganizationExternalReference(
      OrganizationExternalReferenceSaveRequest.fromJS({
        ...values,
        organizationId: organization.id,
      })
    );

    if (result.responseCode === ResponseCode.OK) {
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "externalReferenceSaved" }))
      );
      refresh();
      cleanup();
    }
  };

  const createExternalReference = async (
    values: OrganizationExternalReferenceSaveRequest
  ) => {
    try {
      await saveExternalReference(values);
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const requestUpdateConfirmation = (
    values: OrganizationExternalReferenceSaveRequest
  ) => {
    setPendingUpdate(values);
    confirmUpdateOpenActions.setTrue();
  };

  const deleteExternalReference = async () => {
    const result = await organizationService.deleteOrganizationExternalReference(
      organization.id!,
      externalReference?.id!
    );

    if (result.responseCode === ResponseCode.OK) {
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "externalReferenceDeleted" }))
      );
      refresh();
      cleanup();
    }
  };

  const partnerNameOptions = partnerNames.data?.data ?? [];
  const isRusticiReference = isRustici(externalReference?.partnerName);

  return (
    <Box>
      <LazyTable
        {...{
          rowCount: externalReferences.data?.totalRecords ?? 0,
          rows: (externalReferences.data?.data ?? []).map((reference) => ({
            ...reference,
            id: reference.id!,
          })),
          loading: externalReferences.isFetching,
          titleComponent: (
            <Box sx={styles.header}>
              <Button
                onClick={createOpenActions.setTrue}
                startIcon={<AddIcon />}
              >
                <T id="addExternalReference" />
              </Button>
            </Box>
          ),
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <OrganizationExternalReferenceForm
          onSubmit={createExternalReference}
          partnerNames={partnerNameOptions}
          title={t({ id: "addExternalReference" })}
          onCancel={cleanup}
        />
      </Dialog>

      <Dialog open={updateOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <OrganizationExternalReferenceForm
          onSubmit={requestUpdateConfirmation}
          organizationExternalReference={externalReference}
          partnerNames={partnerNameOptions}
          title={t({ id: "updateExternalReference" })}
          onCancel={cleanup}
        />
      </Dialog>

      <ConfirmDialog
        dialogTitleKey="externalReferenceUpdateTitle"
        rawContent={
          <T
            id={
              isRusticiReference
                ? "externalReferenceUpdateRusticiQuestion"
                : "externalReferenceUpdateQuestion"
            }
            values={{
              partnerName: externalReference?.partnerName,
              externalOrganizationId: externalReference?.externalOrganizationId,
            }}
          />
        }
        open={confirmUpdateOpen}
        onCancel={confirmUpdateOpenActions.setFalse}
        handleConfirm={() => saveExternalReference(pendingUpdate!)}
        confirmLabel="saveButton"
      />

      <ConfirmDialog
        dialogTitleKey="externalReferenceDeleteTitle"
        rawContent={
          <T
            id={
              isRusticiReference
                ? "externalReferenceDeleteRusticiQuestion"
                : "externalReferenceDeleteQuestion"
            }
            values={{
              partnerName: externalReference?.partnerName,
              externalOrganizationId: externalReference?.externalOrganizationId,
            }}
          />
        }
        open={deleteOpen}
        onCancel={cleanup}
        handleConfirm={deleteExternalReference}
        confirmLabel="deleteButton"
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default withErrorBoundary(OrganizationExternalReferences);
